import PropTypes from 'prop-types'
import ModalComponent from 'components/common/ModalComponent'

// Palette cycled per distinct sector/block.
const BLOCK_COLORS = ['#2563eb', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#dc2626']

// 'HH:MM' -> minutes since midnight (null when missing/invalid).
const toMin = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return null
  const [h, m] = hhmm.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return null
  return h * 60 + m
}

const AttendanceTimeline = ({ worker }) => {
  const segments = Array.isArray(worker.segments) ? worker.segments : []

  const entry = toMin(worker.entryTime)
  const exit = toMin(worker.officialEndTime)
  const breakStart = toMin(worker.breakStart)
  const breakEnd = toMin(worker.breakEnd)

  // Axis bounds: cover the attendance window and every segment, with padding.
  const times = [entry, exit]
  segments.forEach(s => { times.push(toMin(s.segStart), toMin(s.segEnd)) })
  const valid = times.filter(t => t != null)
  if (valid.length === 0) {
    return <p className='text-sm text-gray-500'>No time data for this worker.</p>
  }
  let axisMin = Math.min(...valid)
  let axisMax = Math.max(...valid)
  if (axisMax - axisMin < 60) axisMax = axisMin + 60 // guarantee a drawable span
  axisMin = Math.floor(axisMin / 30) * 30
  axisMax = Math.ceil(axisMax / 30) * 30

  const W = 1000
  const padL = 20
  const padR = 20
  const plotW = W - padL - padR
  const x = (min) => padL + ((min - axisMin) / (axisMax - axisMin)) * plotW
  const fmt = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`

  const barTop = 88
  const barH = 54
  const barBottom = barTop + barH

  // Does any sector span the break? Then block_active_at resets to break end.
  const hasBreak = breakStart != null && breakEnd != null && breakEnd > breakStart
  const resets = hasBreak && segments.some(s => {
    const ss = toMin(s.segStart)
    const se = toMin(s.segEnd)
    return ss != null && se != null && ss < breakStart && se > breakEnd
  })

  // Individual scans, clustered by minute (a burst of scans in the same minute
  // becomes one dot with a count) so the marks stay readable.
  const scanCounts = new Map()
  segments.forEach(s => (Array.isArray(s.scans) ? s.scans : []).forEach(t => {
    const m = toMin(t)
    if (m != null) scanCounts.set(m, (scanCounts.get(m) || 0) + 1)
  }))
  const scanClusters = Array.from(scanCounts.entries()).map(([min, count]) => ({ min, count }))
  const scanY = barTop - 18 // scan markers sit in a lane above the segment bars

  // Hour ticks across the axis.
  const ticks = []
  for (let t = Math.ceil(axisMin / 60) * 60; t <= axisMax; t += 60) ticks.push(t)

  return (
    <svg viewBox={`0 56 ${W} 150`} className='w-full' role='img' aria-label='Worker timeline'>
      <defs>
        <pattern id='wt-hatch' width='8' height='8' patternTransform='rotate(45)' patternUnits='userSpaceOnUse'>
          <rect width='8' height='8' fill='#e5e7eb' />
          <line x1='0' y1='0' x2='0' y2='8' stroke='#9ca3af' strokeWidth='3' />
        </pattern>
      </defs>

      {/* gridlines + axis */}
      {ticks.map(t => (
        <line key={`g${t}`} x1={x(t)} y1={64} x2={x(t)} y2={barBottom + 12} stroke='#eef0f3' strokeWidth='1' />
      ))}
      <line x1={padL} y1={barBottom + 12} x2={W - padR} y2={barBottom + 12} stroke='#d1d5db' strokeWidth='1.5' />

      {/* attendance window (faint) */}
      {entry != null && exit != null && (
        <rect x={x(entry)} y={barTop} width={Math.max(0, x(exit) - x(entry))} height={barH} rx='5' fill='#f9fafb' stroke='#e5e7eb' />
      )}

      {/* sector segments */}
      {segments.map((s, i) => {
        const ss = toMin(s.segStart)
        const se = toMin(s.segEnd)
        if (ss == null || se == null) return null
        const w = Math.max(2, x(se) - x(ss))
        const cx = x(ss) + w / 2
        const color = BLOCK_COLORS[i % BLOCK_COLORS.length]
        return (
          <g key={`${s.blockName}-${i}`}>
            <rect x={x(ss)} y={barTop} width={w} height={barH} rx='4' fill={color} />
            {w > 44 && (
              <>
                <text x={cx} y={barTop + 24} fontSize='15' fontWeight='700' fill='#fff' textAnchor='middle'>{s.blockName}</text>
                <text x={cx} y={barTop + 42} fontSize='12' fill='#ffffffcc' textAnchor='middle'>{Number(s.hours).toFixed(2)} h</text>
              </>
            )}
          </g>
        )
      })}

      {/* break overlay */}
      {hasBreak && (
        <g>
          <rect x={x(breakStart)} y={barTop} width={Math.max(2, x(breakEnd) - x(breakStart))} height={barH} fill='url(#wt-hatch)' stroke='#cbd5e1' />
          <text x={(x(breakStart) + x(breakEnd)) / 2} y={barTop + 30} fontSize='10' fontWeight='700' fill='#4b5563' textAnchor='middle'>BREAK</text>
        </g>
      )}

      {/* individual scan markers, in a lane above the bars. Same-minute bursts
          collapse to one dot; the count lives in the hover tooltip to avoid
          crowding the lane with overlapping labels. */}
      {scanClusters.map(({ min, count }) => (
        <g key={`sc${min}`}>
          <line x1={x(min)} y1={scanY + 4} x2={x(min)} y2={barTop} stroke='#c7d2e8' strokeWidth='1' />
          <circle cx={x(min)} cy={scanY} r='4' fill='#ffffff' stroke='#1e3a8a' strokeWidth='1.6' />
          <title>{fmt(min)}{count > 1 ? ` (×${count})` : ''}</title>
        </g>
      ))}

      {/* block_active_at reset marker (below the bar) */}
      {resets && (
        <g>
          <line x1={x(breakEnd)} y1={barBottom} x2={x(breakEnd)} y2={barBottom + 30} stroke='#dc2626' strokeWidth='1.3' strokeDasharray='3 3' />
          <text x={x(breakEnd)} y={barBottom + 44} fontSize='10.5' fontWeight='700' fill='#b91c1c' textAnchor='middle'>block_active_at → {fmt(breakEnd)}</text>
        </g>
      )}

      {/* clock-in flag */}
      {entry != null && (
        <>
          <circle cx={x(entry)} cy={barTop} r='3' fill='#111827' />
          <text x={x(entry) + 6} y={barTop - 4} fontSize='11' fontWeight='600' fill='#111827' textAnchor='start'>Clock-in {fmt(entry)}</text>
        </>
      )}

      {/* time ticks */}
      {ticks.map(t => (
        <text key={`t${t}`} x={x(t)} y={barBottom + 30} fontSize='11.5' fill='#6b7280' textAnchor='middle'>{fmt(t)}</text>
      ))}
    </svg>
  )
}

const WorkerTimelineModal = ({ isOpen, onClose, worker }) => {
  if (!worker) return null
  const title = `Timeline — ${worker.workerName || ''}`
  return (
    <ModalComponent isOpen={isOpen} onClose={onClose} title={title} hideDefaultFooter maxWidth='screen'>
      <div className='space-y-4'>
        <div className='flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600'>
          <span><span className='text-gray-400'>ID:</span> {worker.oldId || worker.workerId}</span>
          <span><span className='text-gray-400'>Group:</span> {worker.groupName || '—'}</span>
          <span><span className='text-gray-400'>Clock-in:</span> {worker.entryTime || '—'}</span>
          <span><span className='text-gray-400'>Official end:</span> {worker.officialEndTime || '—'}</span>
          <span><span className='text-gray-400'>Total:</span> <b className='text-gray-800'>{worker.totalHours} h</b></span>
        </div>

        {(!worker.segments || worker.segments.length === 0) && (
          <p className='text-sm text-amber-600'>No agro scans this day — showing the attendance window only.</p>
        )}

        <div className='rounded-lg border border-gray-200 p-3'>
          <AttendanceTimeline worker={worker} />
        </div>
      </div>
    </ModalComponent>
  )
}

const segmentShape = PropTypes.shape({
  blockName: PropTypes.string,
  firstScan: PropTypes.string,
  lastScan: PropTypes.string,
  segStart: PropTypes.string,
  segEnd: PropTypes.string,
  blockActiveAt: PropTypes.string,
  scans: PropTypes.arrayOf(PropTypes.string),
  hours: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
})

const workerShape = PropTypes.shape({
  workerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  oldId: PropTypes.string,
  workerName: PropTypes.string,
  groupName: PropTypes.string,
  entryTime: PropTypes.string,
  officialEndTime: PropTypes.string,
  breakStart: PropTypes.string,
  breakEnd: PropTypes.string,
  totalHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  segments: PropTypes.arrayOf(segmentShape)
})

AttendanceTimeline.propTypes = { worker: workerShape.isRequired }

WorkerTimelineModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  worker: workerShape
}

export default WorkerTimelineModal
